
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validation
import useVuelidate from '@vuelidate/core'

// classes
import { GatewayMicroserviceT, GatewayRouteT, GatewayScopeT } from '@/core/types/gateway.types'

// constants
import { METHOD_OPTIONS } from '@/core/constants/routes.constants'
import { DataState } from '@/core/classes/DataState.class'
import { gateway } from '@/core/realisations/gateway.realisation'
import { required, requiredIf } from '@vuelidate/validators'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
        checkbox: Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        isBind: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<GatewayRouteT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel', 'delete', 'bind'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            path: null as string | null,
            method: null as string | null,
            summary: null as string | null,
            service_id: null as number | null,
            status: false,
        })

        const RouteValidation = {
            path: {
                required: requiredIf(() => {
                    return !props.isBind
                }),
            },
            method: {
                required: requiredIf(() => {
                    return !props.isBind
                }),
            },
            service_id: { required },
            summary: {
                required: requiredIf(() => {
                    return !props.isBind
                }),
            },
        }

        const state_form$ = useVuelidate(RouteValidation, state)

        const data_state = reactive({
            scopes: new DataState<GatewayScopeT>({}),
            microservices: new DataState<GatewayMicroserviceT>({}),
        })

        const loadScopes = async (page: number) => {
            data_state.scopes.pagination.current_page = page

            try {
                data_state.scopes.is_loading = true

                const response = await gateway.getScopes(page)
                data_state.scopes.list = response.data
                data_state.scopes.pagination.total_pages = response.meta.last_page

                data_state.scopes.is_failed = false
                data_state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.scopes.failure.catchError(error)

                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_failed = true
                }
            } finally {
                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_loading = false
                }
            }
        }

        const loadMoreScopes = async (page: number) => {
            if (page > data_state.scopes.pagination.total_pages) return
            data_state.scopes.pagination.current_page = page

            try {
                data_state.scopes.is_loading = true

                const response = await gateway.getScopes(page)
                data_state.scopes.list = data_state.scopes.list.concat(response.data)
                data_state.scopes.pagination.total_pages = response.meta.last_page

                data_state.scopes.is_failed = false
                data_state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.scopes.failure.catchError(error)

                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_failed = true
                }
            } finally {
                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_loading = false
                }
            }
        }

        const loadMicroservices = async (page: number) => {
            data_state.microservices.pagination.current_page = page

            try {
                data_state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page, undefined, {
                    name: undefined,
                    isActive: 0,
                })
                data_state.microservices.list = response.data
                data_state.microservices.pagination.total_pages = response.meta.last_page

                data_state.microservices.is_failed = false
                data_state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.microservices.failure.catchError(error)

                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_failed = true
                }
            } finally {
                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_loading = false
                }
            }
        }

        const loadMoreMicroservices = async (page: number) => {
            if (page > data_state.microservices.pagination.total_pages) return
            data_state.microservices.pagination.current_page = page

            try {
                data_state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page, undefined, {
                    name: undefined,
                    isActive: 0,
                })
                data_state.microservices.list = data_state.microservices.list.concat(response.data)
                data_state.microservices.pagination.total_pages = response.meta.last_page

                data_state.microservices.is_failed = false
                data_state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.microservices.failure.catchError(error)

                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_failed = true
                }
            } finally {
                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_loading = false
                }
            }
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление роута'
            if (props.isUpdate) return 'Редактирование роута'
            if (props.isDelete) return 'Удаление скоупа'
            if (props.isBind) return 'Закрепление скоупа'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            if (props.isBind) return 'Закрепить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        const emitDelete = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('delete', toRaw(state))
        }

        const emitBind = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return
            emit('bind', state.service_id, props.initialValue.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
            if (props.isBind) emitBind()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            if (props.isBind) loadScopes(1)
            else loadMicroservices(1)

            if (!props.initialValue || props.isBind) return

            state.id = props.initialValue.id
            state.path = props.initialValue.path
            state.method = props.initialValue.method
            state.summary = props.initialValue.summary
            state.service_id = props.initialValue.service_id
            state.status = props.initialValue.status
        })

        return {
            state,
            data_state,
            state_form$,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadMicroservices,
            loadMoreMicroservices,
            loadScopes,
            loadMoreScopes,
            METHOD_OPTIONS,
        }
    },
})
